import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { addDays, format, subDays } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import firebaseDataService from '../../services/FirebaseDataService';
import * as shiftDataService from '../../services/migration/shiftDataService';
import type { ScanEvent, ScanEventPerson, ScanEventSummary } from '../../services/migration/shiftDataService';

/**
 * Okutma Kayıtları — vardiya okutma kayıt defteri.
 * Üstte sayaçlar, solda personel bazında davranış, sağda seçilen personelin bir gününün akışı.
 * Personel adları personel listesinden gelir (e-posta gösterilmez).
 */
interface OkutmaKayitlariProps {
  branchId?: string;
  isManager?: boolean;
}

const COLORS = {
  in: '#2e7d32',
  out: '#1a237e',
  warn: '#e65100',
  fail: '#c62828',
  mute: '#607d8b',
  edit: '#6a1b9a',
};

const ymd = (d: Date) => format(d, 'yyyy-MM-dd');

const hhmmss = (iso?: string | null, withSeconds = true) => {
  if (!iso) return '--:--';
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return withSeconds ? `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}` : `${p(d.getHours())}:${p(d.getMinutes())}`;
};

const OkutmaKayitlari: React.FC<OkutmaKayitlariProps> = ({ branchId, isManager = false }) => {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language?.toLowerCase().startsWith('en');
  const tr = (trText: string, enText: string) => (isEnglish ? enText : trText);
  const { userDetails } = useAuth();

  const [from, setFrom] = useState(ymd(subDays(new Date(), 29)));
  const [to, setTo] = useState(ymd(new Date()));
  const [summary, setSummary] = useState<ScanEventSummary | null>(null);
  const [personnel, setPersonnel] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  const [day, setDay] = useState(ymd(new Date()));
  const [events, setEvents] = useState<ScanEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  // Personel adları (ve yönetici için şube filtresi).
  useEffect(() => {
    const companyId = userDetails?.companyId;
    if (!companyId) return;
    firebaseDataService
      .getPersonnel(companyId)
      .then((result: any) => {
        const map: Record<string, any> = {};
        if (Array.isArray(result)) result.forEach((p: any) => { map[p.id] = p; });
        else if (result) Object.keys(result).forEach((id) => { map[id] = result[id]; });
        setPersonnel(map);
      })
      .catch(() => setPersonnel({}));
  }, [userDetails?.companyId]);

  const inBranch = useCallback(
    (uid: string) => {
      if (!isManager || !branchId) return true;
      const p = personnel[uid];
      if (!p) return false;
      const b = p.branchesId || p.branchId || p.branch;
      if (!b) return false;
      if (typeof b === 'string') return b === branchId;
      if (typeof b === 'object') return b.id === branchId || Object.keys(b).includes(branchId);
      return false;
    },
    [isManager, branchId, personnel],
  );

  const nameOf = useCallback(
    (uid: string) => {
      const p = personnel[uid];
      const name = p?.name || [p?.firstName, p?.lastName].filter(Boolean).join(' ');
      return name || tr('Bilinmeyen personel', 'Unknown personnel');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [personnel, isEnglish],
  );

  const loadSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSummary(await shiftDataService.getScanEventSummary({ from, to }));
    } catch (e: any) {
      setError(e?.message || tr('Okutma kayıtları yüklenemedi.', 'Could not load scan records.'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  // Seçilen günün akışı: o gün + ertesi sabah (gece yarısını geçen kapanışlar).
  useEffect(() => {
    if (!selectedUid) {
      setEvents([]);
      return;
    }
    let cancelled = false;
    setEventsLoading(true);
    const next = ymd(addDays(new Date(`${day}T12:00:00`), 1));
    shiftDataService
      .getScanEvents({ personnelUid: selectedUid, from: day, to: next, pageSize: 500 })
      .then((r) => {
        if (cancelled) return;
        // Ertesi günden yalnız sabah 06:00'ya kadar olanlar (gece kapanışı).
        const limit = new Date(`${next}T06:00:00`).getTime();
        setEvents(
          r.items
            .filter((e) => new Date(e.eventTime).getTime() < limit)
            .sort((a, b) => new Date(a.eventTime).getTime() - new Date(b.eventTime).getTime()),
        );
      })
      .catch(() => !cancelled && setEvents([]))
      .finally(() => !cancelled && setEventsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [selectedUid, day]);

  const people = useMemo(
    () => (summary?.people ?? []).filter((p) => inBranch(p.personnelUid)),
    [summary, inBranch],
  );

  const totals = useMemo(() => {
    const t = {
      scans: 0, warnings: 0, corrected: 0, abandoned: 0, missingOut: 0, orphanOut: 0,
      failures: 0, managerEdits: 0, reminders: 0,
    };
    people.forEach((p) => {
      t.scans += p.scans; t.warnings += p.warnings; t.corrected += p.corrected; t.abandoned += p.abandoned;
      t.missingOut += p.missingOut; t.orphanOut += p.orphanOut; t.failures += p.failures;
      t.managerEdits += p.managerEdits; t.reminders += p.reminders;
    });
    return t;
  }, [people]);

  const selectPerson = (p: ScanEventPerson) => {
    setSelectedUid(p.personnelUid);
    if (p.lastEventTime) setDay(ymd(new Date(p.lastEventTime)));
  };

  const chip = (value: number, label: string, color?: string) => (
    <Paper variant="outlined" sx={{ px: 1.5, py: 1, minWidth: 110 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, color: value > 0 && color ? color : 'text.primary', lineHeight: 1.2 }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Paper>
  );

  const countCell = (value: number, color: string) =>
    value > 0 ? (
      <Chip size="small" label={value} sx={{ bgcolor: `${color}1A`, color, fontWeight: 700, height: 22 }} />
    ) : (
      <Typography variant="body2" color="text.disabled">0</Typography>
    );

  const dirTag = (dir?: string | null) =>
    dir === 'in' || dir === 'out' ? (
      <Chip
        size="small"
        label={dir === 'in' ? tr('GİRİŞ', 'IN') : tr('ÇIKIŞ', 'OUT')}
        sx={{ height: 20, fontWeight: 700, bgcolor: dir === 'in' ? `${COLORS.in}1A` : `${COLORS.out}1A`, color: dir === 'in' ? COLORS.in : COLORS.out, mr: 0.5 }}
      />
    ) : null;

  const reminderLabel = (kind: string) =>
    ({
      exit_before_30: tr('çıkıştan 30 dk önce', '30 min before exit'),
      exit_before_15: tr('çıkıştan 15 dk önce', '15 min before exit'),
      exit_missed: tr('çıkış görünmüyor', 'exit missing'),
      entry_after_30: tr('giriş görünmüyor (30 dk)', 'entry missing (30 min)'),
      entry_after_45: tr('giriş görünmüyor (45 dk)', 'entry missing (45 min)'),
    } as Record<string, string>)[kind] || kind;

  // Tek bir olayın akıştaki satırı: renk, başlık, alt bilgi.
  const describe = (e: ScanEvent, idx: number): { color: string; title: React.ReactNode; meta?: string } => {
    const app = [e.appVersion ? tr(`uygulama ${e.appVersion}`, `app ${e.appVersion}`) : null, e.platform]
      .filter(Boolean)
      .join(' · ');
    const prevWarn = [...events.slice(0, idx)].reverse().find((x) => x.eventType === 'scan' && x.outcome === 'confirm_required');

    if (e.eventType === 'manager_edit') {
      const what =
        e.outcome === 'punch_add' ? tr('ekledi', 'added')
          : e.outcome === 'punch_delete' ? tr('sildi', 'deleted')
            : e.outcome === 'punch_resave' ? tr('değiştirmeden kaydetti', 'saved without change')
              : tr('düzeltti', 'edited');
      return {
        color: COLORS.edit,
        title: <span><b>{e.actorName || tr('Yönetici', 'Manager')}</b> {what}</span>,
        meta: [e.oldValue, e.newValue].filter(Boolean).join(' → '),
      };
    }
    if (e.eventType === 'client_failure') {
      const reached = events.some((x) => x.eventType === 'scan' && x.clientEventId && x.clientEventId === e.clientEventId);
      return {
        color: COLORS.fail,
        title: <span>{dirTag(e.qrDirection)}{tr('telefon kaydedemedi', 'phone could not save')} ({e.outcome === 'timeout' ? tr('zaman aşımı', 'timeout') : tr('bağlantı', 'connection')})</span>,
        meta: [
          e.clientTime ? tr(`okutma anı ${hhmmss(e.clientTime)}`, `scanned at ${hhmmss(e.clientTime)}`) : null,
          reached ? tr('istek aslında sunucuya ulaşmış', 'request actually reached the server') : null,
          app,
        ].filter(Boolean).join(' · '),
      };
    }
    if (e.eventType === 'reminder') {
      let at = '';
      try { at = hhmmss(JSON.parse(e.detailJson || '{}').scheduledFor, false); } catch { /* yok */ }
      return {
        color: COLORS.mute,
        title: <span>{tr('hatırlatma kuruldu', 'reminder scheduled')}: {reminderLabel(e.outcome)}</span>,
        meta: at ? tr(`bildirim saati ${at}`, `fires at ${at}`) : undefined,
      };
    }

    // scan
    switch (e.outcome) {
      case 'recorded':
        return { color: e.recordedDirection === 'out' ? COLORS.out : COLORS.in, title: <span>{dirTag(e.recordedDirection)}{tr('kaydedildi', 'recorded')}</span>, meta: app };
      case 'confirm_required':
        return {
          color: COLORS.warn,
          title: <span>{dirTag(e.qrDirection)}{tr('kodu okutuldu,', 'scanned,')} <b>{tr('uyarı gösterildi', 'warning shown')}</b></span>,
          meta: [
            e.openInTime ? tr(`açık giriş: ${hhmmss(e.openInTime, false)}`, `open entry: ${hhmmss(e.openInTime, false)}`) : tr('açık giriş yok', 'no open entry'),
            tr('kayıt yapılmadı', 'nothing recorded'),
          ].join(' · '),
        };
      case 'confirmed':
      case 'corrected': {
        const secs = prevWarn ? Math.round((new Date(e.eventTime).getTime() - new Date(prevWarn.eventTime).getTime()) / 1000) : null;
        return {
          color: e.outcome === 'corrected' ? (e.recordedDirection === 'out' ? COLORS.out : COLORS.in) : COLORS.warn,
          title: (
            <span>
              {dirTag(e.recordedDirection)}{tr('kaydedildi,', 'recorded,')}{' '}
              <b>{e.outcome === 'corrected' ? tr('personel düzeltti', 'corrected by staff') : tr('personel onayladı', 'confirmed by staff')}</b>
            </span>
          ),
          meta: secs != null ? tr(`uyarıdan ${secs} sn sonra`, `${secs}s after the warning`) : app,
        };
      }
      case 'deduped':
        return { color: COLORS.mute, title: <span>{dirTag(e.qrDirection)}{tr('zaten kayıtlıydı (tekrar okutma)', 'already recorded (repeat scan)')}</span>, meta: app };
      default:
        return {
          color: COLORS.mute,
          title: <span>{dirTag(e.qrDirection)}{tr('okutma kabul edilmedi', 'scan not accepted')} ({e.outcome})</span>,
          meta: e.messageShown || app,
        };
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1.5 }}>
        <TextField
          type="date" size="small" label={tr('Başlangıç', 'From')} value={from}
          onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }}
        />
        <TextField
          type="date" size="small" label={tr('Bitiş', 'To')} value={to}
          onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }}
        />
        <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={loadSummary} disabled={loading}>
          {tr('Yenile', 'Refresh')}
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {tr('Kayıt defteri yeni uygulama sürümüyle dolmaya başlar; en fazla 62 gün seçilebilir.',
            'The log fills from the new app version on; up to 62 days can be selected.')}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {summary?.logDisabled && (
        <Alert severity="info" sx={{ mb: 1 }}>
          {tr('Bu şirkette okutma kayıt defteri henüz kurulmamış. Aşağıda yalnız eksik çıkış ve girişsiz çıkış sayıları görünür.',
            'The scan log is not set up for this company yet. Only missing-exit and exit-without-entry counts are shown below.')}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {chip(totals.scans, tr('okutma', 'scans'))}
        {chip(totals.warnings, tr('yanlış yön uyarısı', 'wrong-direction warnings'), COLORS.warn)}
        {chip(totals.corrected, tr('personel düzeltti', 'corrected by staff'), COLORS.in)}
        {chip(totals.abandoned, tr('uyarıdan sonra okutmadı', 'left after warning'), COLORS.fail)}
        {chip(totals.missingOut, tr('çıkış okutulmadı', 'exit not scanned'), COLORS.out)}
        {chip(totals.orphanOut, tr('girişsiz çıkış', 'exit without entry'), COLORS.out)}
        {chip(totals.failures, tr('bağlantı hatası', 'connection errors'), COLORS.fail)}
        {chip(totals.managerEdits, tr('yönetici düzeltmesi', 'manager edits'), COLORS.edit)}
        {chip(totals.reminders, tr('hatırlatma', 'reminders'))}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 3fr) minmax(0, 2fr)' }, gap: 2 }}>
        <Paper variant="outlined" sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider' }}>
            {tr('Personel davranışı', 'Personnel behaviour')}
          </Typography>
          {loading ? (
            <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={28} /></Box>
          ) : people.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              {tr('Bu aralıkta kayıt yok.', 'No records in this range.')}
            </Typography>
          ) : (
            <TableContainer sx={{ maxHeight: 520 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>{tr('Personel', 'Personnel')}</TableCell>
                    <TableCell align="center">{tr('Oturum', 'Sessions')}</TableCell>
                    <TableCell align="center">{tr('Yanlış yön', 'Wrong dir.')}</TableCell>
                    <TableCell align="center">{tr('Düzeltti', 'Corrected')}</TableCell>
                    <TableCell align="center">{tr('Çıkış yok', 'No exit')}</TableCell>
                    <TableCell align="center">{tr('Girişsiz çıkış', 'Exit w/o entry')}</TableCell>
                    <TableCell align="center">{tr('Bağlantı', 'Connection')}</TableCell>
                    <TableCell align="center">{tr('Yönetici', 'Manager')}</TableCell>
                    <TableCell>{tr('Sürüm', 'Version')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {people.map((p) => (
                    <TableRow
                      key={p.personnelUid}
                      hover
                      selected={p.personnelUid === selectedUid}
                      onClick={() => selectPerson(p)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>{nameOf(p.personnelUid)}</TableCell>
                      <TableCell align="center">{p.completeSessions}</TableCell>
                      <TableCell align="center">{countCell(p.warnings, COLORS.warn)}</TableCell>
                      <TableCell align="center">{countCell(p.corrected, COLORS.in)}</TableCell>
                      <TableCell align="center">{countCell(p.missingOut, COLORS.out)}</TableCell>
                      <TableCell align="center">{countCell(p.orphanOut, COLORS.out)}</TableCell>
                      <TableCell align="center">{countCell(p.failures, COLORS.fail)}</TableCell>
                      <TableCell align="center">{countCell(p.managerEdits, COLORS.edit)}</TableCell>
                      <TableCell>
                        <Typography variant="caption" color={p.appVersion ? 'text.primary' : 'text.disabled'}>
                          {p.appVersion || tr('eski', 'old')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ minWidth: 0 }}>
          <Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" sx={{ flex: 1, minWidth: 0 }}>
              {selectedUid ? nameOf(selectedUid) : tr('Günün akışı', 'Day timeline')}
            </Typography>
            <TextField
              type="date" size="small" value={day} onChange={(e) => setDay(e.target.value)}
              disabled={!selectedUid} sx={{ width: 160 }}
            />
          </Box>
          {!selectedUid ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              {tr('Akışı görmek için soldan bir personel seçin.', 'Select a person on the left to see the timeline.')}
            </Typography>
          ) : eventsLoading ? (
            <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={28} /></Box>
          ) : events.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              {tr('Bu gün için kayıt yok.', 'No records for this day.')}
            </Typography>
          ) : (
            <Box sx={{ p: 1.5, maxHeight: 520, overflowY: 'auto' }}>
              {events.map((e, idx) => {
                const d = describe(e, idx);
                const nextDay = ymd(new Date(e.eventTime)) !== day;
                return (
                  <Box key={e.id} sx={{ display: 'grid', gridTemplateColumns: '78px 14px 1fr', gap: 1, py: 0.75, alignItems: 'start' }}>
                    <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                      {hhmmss(e.eventTime)}
                      {nextDay && (
                        <Typography component="span" variant="caption" sx={{ display: 'block', lineHeight: 1 }}>
                          {tr('ertesi gün', 'next day')}
                        </Typography>
                      )}
                    </Typography>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: d.color, mt: '6px' }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" component="div">{d.title}</Typography>
                      {d.meta && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{d.meta}</Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default OkutmaKayitlari;
